import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import MySubscription from './pages/MySubscription';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import MyInvoices from './pages/MyInvoices';
import Settings from './pages/Settings';
import RiskAnalysis from './pages/RiskAnalysis';
import HaliLeads from './pages/HaliLeads';
import ContractAnalyzer from './pages/ContractAnalyzer';
import InvoiceFinancing from './pages/InvoiceFinancing';
import PastTradeStudio from './pages/PastTradeStudio';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import './App.css';

import CustomerDashboard from './pages/CustomerDashboard';
import PortalLayout from './components/PortalLayout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    const initializeAuth = async () => {
      // Check if email passed in URL (for cross-domain admin access)
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      
      if (urlEmail) {
        const currentUser = localStorage.getItem('halisoft_user');
        if (!currentUser) {
          // Auto-login with email from URL - create REAL session via API
          const decodedEmail = decodeURIComponent(urlEmail);
          console.log('[ProtectedRoute] Auto-login with email from URL:', decodedEmail);
          
          try {
            // Try to login via API to create a real session/token
            const api = (await import('./api/client')).default;
            const response = await api.post('/api/auth/login', {
              email: decodedEmail,
              password: '1234' // Default password for demo users
            });
            
            if (response.data.token) {
              localStorage.setItem('halisoft_token', response.data.token);
              localStorage.setItem('halisoft_user', JSON.stringify(response.data.user || { email: decodedEmail, isAdmin: true }));
              localStorage.setItem('halisoft_email', decodedEmail);
              localStorage.setItem('halisoft_admin_email', decodedEmail); // Store for admin endpoints
              // Remove email param but preserve other params (like module)
              const newParams = new URLSearchParams(window.location.search);
              newParams.delete('email');
              const newSearch = newParams.toString();
              const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
              window.history.replaceState({}, '', newUrl);
              setIsInitialized(true);
              return;
            }
          } catch (err) {
            console.warn('[ProtectedRoute] API login failed, using localStorage fallback:', err);
            // Fallback: just use localStorage if API fails
            localStorage.setItem('halisoft_email', decodedEmail);
            localStorage.setItem('halisoft_admin_email', decodedEmail); // Store for admin endpoints
            localStorage.setItem('halisoft_user', JSON.stringify({ email: decodedEmail, isAdmin: true }));
            // Remove email param but preserve other params (like module)
            const newParams = new URLSearchParams(window.location.search);
            newParams.delete('email');
            const newSearch = newParams.toString();
            const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, '', newUrl);
            setIsInitialized(true);
            return;
          }
        }
      }
      
      setIsInitialized(true);
    };
    
    initializeAuth();
  }, []);
  
  // Show loading while processing URL email
  if (!isInitialized) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }
  
  // Check localStorage AFTER processing URL params
  const user = localStorage.getItem('halisoft_user');
  const email = localStorage.getItem('halisoft_email');

  if (!user && !email) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const user = localStorage.getItem('halisoft_user');

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <div className="App">
          <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />

          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />

          <Route
            path="/pricing"
            element={<LandingPage />}
          />

          {/* Protected Routes with Layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <PortalLayout>
                  <CustomerDashboard />
                </PortalLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <PortalLayout>
                  <MySubscription />
                </PortalLayout>
              </ProtectedRoute>
            }
          />

          <Route path="/subscription/success" element={<PaymentSuccess />} />
          <Route path="/subscription/cancel" element={<PaymentCancel />} />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <PortalLayout>
                  <MyInvoices />
                </PortalLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <PortalLayout>
                  <Settings />
                </PortalLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/risk-analysis"
            element={<RiskAnalysis />}
          />

          <Route
            path="/hali-leads"
            element={<HaliLeads />}
          />

          <Route
            path="/contract-analyzer"
            element={<ContractAnalyzer />}
          />

          <Route
            path="/invoice-financing"
            element={<InvoiceFinancing />}
          />

          <Route
            path="/past-trade-studio"
            element={<PastTradeStudio />}
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
